/*
 * nono-booking.js — shared booking-modal controller.
 *
 * Binds every [data-booking-trigger] on the page to the single
 * #lp-booking-modal (rendered once by snippets/booking-modal.liquid).
 *
 * - Lazy-injects the Momence iframe on first open (fast LCP).
 * - Optional pre-warm: if the modal has data-booking-prewarm="1",
 *   injects the iframe hidden once the page goes idle so the first
 *   click is instant.
 * - Loading skeleton shown until the iframe fires `load`.
 * - Fires fbq('track','InitiateCheckout') on open (deduped per page view).
 * - Best-effort branded success overlay if Momence emits a recognisable
 *   postMessage (cross-origin: we can't read its DOM/URL, only messages).
 * - ESC + backdrop close, basic focus handling, body scroll lock.
 */
(function () {
  'use strict';

  function init() {
    var modal = document.getElementById('lp-booking-modal');
    if (!modal || modal.dataset.bookingBound === '1') return;
    modal.dataset.bookingBound = '1';

    var host = modal.querySelector('[data-booking-iframe-host]');
    var skeleton = modal.querySelector('[data-booking-skeleton]');
    var success = modal.querySelector('[data-booking-success]');
    var iframeUrl = modal.getAttribute('data-booking-iframe-url');
    var prewarm = modal.getAttribute('data-booking-prewarm') === '1';

    var iframeBuilt = false;
    var iframeLoads = 0;
    var modalEverOpened = false;
    var initiateFired = false;
    var lastFocused = null;

    function buildIframe() {
      if (iframeBuilt) return;
      iframeBuilt = true;
      var ifr = document.createElement('iframe');
      ifr.setAttribute('src', iframeUrl);
      ifr.setAttribute('allow', 'payment; clipboard-write');
      ifr.setAttribute('title', 'Book gratis prøvetime hos Nono Moss');
      ifr.setAttribute('loading', 'eager');
      ifr.addEventListener('load', function () {
        iframeLoads += 1;
        if (iframeLoads === 1) {
          // First load = the Momence calendar. Hide the loading skeleton.
          if (skeleton) {
            skeleton.classList.add('is-fading');
            setTimeout(function () { skeleton.setAttribute('hidden', ''); }, 280);
          }
          return;
        }
        // A later full navigation of the (otherwise SPA) Momence iframe means
        // Momence redirected after a completed booking (to /pages/booket).
        // Shopify serves that page with X-Frame-Options: DENY so it cannot
        // render in-frame (the frame goes blank) - so we show our branded
        // overlay over it instead. Guarded by modalEverOpened so a stray
        // reload during pre-warm (before the visitor opened the modal) can't
        // trigger it.
        if (modalEverOpened) {
          showSuccess();
        }
      });
      host.appendChild(ifr);
    }

    function fireInitiateCheckout() {
      if (initiateFired || !window.fbq) return;
      initiateFired = true;
      try {
        var catEl = document.querySelector('[data-page-category]');
        window.fbq('track', 'InitiateCheckout', {
          content_category: catEl ? catEl.getAttribute('data-page-category') : 'booking',
          content_name: document.title
        });
      } catch (e) {}
    }

    function openModal() {
      modalEverOpened = true;
      buildIframe();
      modal.removeAttribute('hidden');
      document.documentElement.style.overflow = 'hidden';
      lastFocused = document.activeElement;
      var closeBtn = modal.querySelector('[data-booking-close]');
      if (closeBtn && closeBtn.focus) {
        try { closeBtn.focus({ preventScroll: true }); } catch (e) { closeBtn.focus(); }
      }
      fireInitiateCheckout();
    }

    function closeModal() {
      modal.setAttribute('hidden', '');
      document.documentElement.style.overflow = '';
      if (lastFocused && lastFocused.focus) {
        try { lastFocused.focus({ preventScroll: true }); } catch (e) {}
      }
    }

    // Bind all triggers (works for elements that exist now; we also
    // delegate from document for any added later, e.g. by app blocks).
    function bindTrigger(el) {
      if (!el || el.dataset.bookingTriggerBound === '1') return;
      el.dataset.bookingTriggerBound = '1';
      el.addEventListener('click', function (e) {
        e.preventDefault();
        openModal();
      });
    }
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-booking-trigger]'),
      bindTrigger
    );
    // Delegated handling. Catches:
    //  - any [data-booking-trigger] added after init (app blocks etc.)
    //  - the global header "Prøv Nono" CTA which links to #memberCreateForm
    //    or #lp-booking. On landing pages (where this modal exists) those
    //    should open the booking modal instead of jumping to a dead anchor.
    //    On pages without the modal, this script isn't loaded, so the
    //    header CTA keeps its normal scroll behaviour.
    var HEADER_CTA_SELECTOR = 'a[href="#memberCreateForm"], a[href="#lp-booking"]';
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var trigger = e.target.closest('[data-booking-trigger]');
      if (trigger && trigger.dataset.bookingTriggerBound !== '1') {
        e.preventDefault();
        openModal();
        return;
      }
      var headerCta = e.target.closest(HEADER_CTA_SELECTOR);
      if (headerCta) {
        e.preventDefault();
        openModal();
      }
    });

    Array.prototype.forEach.call(
      modal.querySelectorAll('[data-booking-close]'),
      function (c) {
        c.addEventListener('click', function (e) {
          e.preventDefault();
          closeModal();
        });
      }
    );

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hasAttribute('hidden')) closeModal();
    });

    // Success detection.
    //  1. Primary: our own /pages/booket confirmation page (loaded inside
    //     this iframe after a Momence booking) posts a same-origin message.
    //  2. Fallback: best-effort match on any postMessage Momence itself
    //     might send (cross-origin: we can only read messages, not its DOM).
    function showSuccess() {
      if (success) success.classList.add('is-visible');
    }
    window.addEventListener('message', function (e) {
      // (1) Our own confirmation page — same origin as this parent page.
      if (e.origin === window.location.origin) {
        var od = e.data;
        if (od && (od.type === 'nono:booking-complete' || od === 'nono:booking-complete')) {
          showSuccess();
        }
        return;
      }
      // (2) Momence cross-origin best-effort fallback.
      if (!e.origin || e.origin.indexOf('momence.com') === -1) return;
      var d = e.data;
      var text = '';
      try {
        text = typeof d === 'string' ? d : JSON.stringify(d || '');
      } catch (err) { text = ''; }
      if (!text) return;
      var looksLikeBooking = /book(ing|ed)|reserv|confirm|success|complete/i.test(text);
      if (looksLikeBooking) {
        showSuccess();
      }
    });

    // Optional pre-warm so the first click is instant. Warm on the first
    // real interaction (visitor engaged -> likely to reach a CTA soon),
    // with an idle/timeout fallback if they never interact.
    if (prewarm) {
      var warmed = false;
      var warm = function () {
        if (warmed) return;
        warmed = true;
        buildIframe();
      };
      ['pointerdown', 'touchstart', 'scroll', 'keydown', 'mousemove'].forEach(function (ev) {
        window.addEventListener(ev, warm, { once: true, passive: true });
      });
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(warm, { timeout: 6000 });
      } else {
        setTimeout(warm, 4000);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
